/**
 * @module utils/columnSort/shared/fieldUtils
 * @description 与旧 fieldColumnSort/utils 对齐的字段匹配、聚合展示名、占比列识别
 */

import type {
  IColumnSortItem,
  IColumnSortFieldInfo,
  TBuilderType,
} from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  COLUMN_SORT_BUILDER_DIMENSION,
  COLUMN_SORT_BUILDER_METRIC,
  isColumnSortDimension,
  normalizeColumnSortItem,
  PERCENTAGE_PREFIX,
  stripPercentagePrefix,
} from './keys';
import { isDimPctVirtualColumn } from './dimensionPercentage';

export function getFieldIdentityKey(item: {
  table_code?: string;
  table_id?: string;
  column?: string;
  column_name?: string;
}): string {
  const tableCode = item?.table_code || 't0';
  const tableId = item?.table_id || '';
  const column = item?.column_name || item?.column || '';
  return `${tableCode}_${tableId}_${column}`;
}

export function getAggregateValue(metric?: { aggregate?: string }): string {
  return (metric?.aggregate || '').trim();
}

export function applyAggregatePrefix(
  value: string | undefined,
  aggregate: string,
): string {
  if (!aggregate || !value) {
    return value || '';
  }
  const hasPercent = value.startsWith(PERCENTAGE_PREFIX);
  const rawValue = hasPercent
    ? value.slice(PERCENTAGE_PREFIX.length)
    : value;
  if (rawValue.startsWith(`${aggregate}_`)) {
    return hasPercent ? `${PERCENTAGE_PREFIX}${rawValue}` : rawValue;
  }
  const prefixedValue = `${aggregate}_${rawValue}`;
  return hasPercent ? `${PERCENTAGE_PREFIX}${prefixedValue}` : prefixedValue;
}

export function buildMetricDisplayInfo(
  metric: {
    column_name?: string;
    column?: string;
    column_display?: string;
    column_display_en?: string;
    aggregate?: string;
  },
  hasDuplicate: boolean,
): {
  column: string;
  column_display_cn: string;
  column_display_en: string;
} {
  const baseColumn = metric?.column_name || metric?.column || '';
  const baseDisplayCn = metric?.column_display || baseColumn;
  const baseDisplayEn =
    metric?.column_display_en || metric?.column_display || baseColumn;
  const aggregate = getAggregateValue(metric);
  if (hasDuplicate && aggregate) {
    return {
      column: baseColumn,
      column_display_cn: `${aggregate}_${baseDisplayCn}`,
      column_display_en: `${aggregate}_${baseDisplayEn}`,
    };
  }
  return {
    column: baseColumn,
    column_display_cn: baseDisplayCn,
    column_display_en: baseDisplayEn,
  };
}

export function hasSameDimensionField({
  fieldInfo,
  columnDimensions,
  columnSort,
}: {
  fieldInfo: IColumnSortFieldInfo;
  columnDimensions?: unknown[];
  columnSort?: IColumnSortItem[];
}): boolean {
  const baseColumn = fieldInfo.column_name || fieldInfo.column || '';
  const fieldKey = getFieldIdentityKey({
    table_code: fieldInfo.table_code,
    table_id: fieldInfo.table_id,
    column: baseColumn,
  });

  if (Array.isArray(columnDimensions)) {
    return columnDimensions.some(raw => {
      const row = raw as Record<string, unknown>;
      return (
        getFieldIdentityKey({
          table_code: String(row.table_code ?? 't0'),
          table_id: String(row.table_id ?? ''),
          column: String(row.column_name ?? row.column ?? ''),
        }) === fieldKey
      );
    });
  }

  if (!Array.isArray(columnSort)) {
    return false;
  }

  return columnSort.some(item => {
    if (!isColumnSortDimension(item)) {
      return false;
    }
    if (item.is_compare_date) {
      return false;
    }
    return getFieldIdentityKey(item) === fieldKey;
  });
}

export function findMetricInfo(
  columnMetrics: unknown[] | undefined,
  fieldInfo: IColumnSortFieldInfo,
): Record<string, unknown> | undefined {
  if (!Array.isArray(columnMetrics)) {
    return undefined;
  }
  const baseColumn = fieldInfo.column_name || fieldInfo.column || '';
  const fieldKey = getFieldIdentityKey({
    table_code: fieldInfo.table_code,
    table_id: fieldInfo.table_id,
    column: baseColumn,
  });
  const matched = columnMetrics.find(raw => {
    const row = raw as Record<string, unknown>;
    return (
      getFieldIdentityKey({
        table_code: String(row.table_code ?? 't0'),
        table_id: String(row.table_id ?? ''),
        column: String(row.column_name ?? row.column ?? ''),
      }) === fieldKey
    );
  });
  return matched as Record<string, unknown> | undefined;
}

export function resolveFieldAggregate(
  fieldInfo: IColumnSortFieldInfo,
  columnMetrics?: unknown[],
): string {
  const metricInfo = findMetricInfo(columnMetrics, fieldInfo);
  return getAggregateValue(
    (metricInfo || fieldInfo) as { aggregate?: string },
  );
}

export function isPercentageColumn(
  item: IColumnSortItem,
  metricColumnName: string,
): boolean {
  if (isColumnSortDimension(item)) {
    return false;
  }
  const percentageColumnName = `${PERCENTAGE_PREFIX}${metricColumnName}`;
  return (
    item.column === percentageColumnName ||
    item.column_display_cn === percentageColumnName ||
    item.column_display_en === percentageColumnName ||
    item.column === `%${metricColumnName}`
  );
}

export function isPercentVirtualColumn(item: IColumnSortItem): boolean {
  if (isColumnSortDimension(item)) {
    return false;
  }
  const col = item.column || '';
  return col.startsWith(PERCENTAGE_PREFIX) || col.startsWith('%');
}

/** 列占比 % 或维度占比 dim_pct 等「挂在源 metric 后」的虚拟列 */
export function isMetricAttachedVirtualColumn(item: IColumnSortItem): boolean {
  return isPercentVirtualColumn(item) || isDimPctVirtualColumn(item);
}

export function percentColumnForMetric(
  metricName: string,
  displayCn: string,
  displayEn: string,
): Pick<IColumnSortItem, 'column' | 'column_display_cn' | 'column_display_en'> {
  return {
    column: `${PERCENTAGE_PREFIX}${metricName}`,
    column_display_cn: `${PERCENTAGE_PREFIX}${displayCn}`,
    column_display_en: `${PERCENTAGE_PREFIX}${displayEn}`,
  };
}

export function isSameFieldColumnSortItem(
  item: IColumnSortItem,
  fieldInfo: IColumnSortFieldInfo,
  aggregate?: string,
): boolean {
  const baseColumn = fieldInfo.column_name || fieldInfo.column || '';
  if (!baseColumn) {
    return false;
  }
  if (
    (item.table_code || 't0') !== (fieldInfo.table_code || 't0') ||
    String(item.table_id ?? '') !== String(fieldInfo.table_id ?? '')
  ) {
    return false;
  }
  const normalizedColumn = stripPercentagePrefix(item.column);
  if (!normalizedColumn) {
    return false;
  }
  if (normalizedColumn === baseColumn) {
    return true;
  }
  if (aggregate && normalizedColumn === `${aggregate}_${baseColumn}`) {
    return true;
  }
  return false;
}

export function resolveMetricColumnName({
  fieldInfo,
  columnSort,
  columnDimensions,
  columnMetrics,
}: {
  fieldInfo: IColumnSortFieldInfo;
  columnSort?: IColumnSortItem[];
  columnDimensions?: unknown[];
  columnMetrics?: unknown[];
}): string {
  const metricInfo =
    findMetricInfo(columnMetrics, fieldInfo) ||
    (fieldInfo as Record<string, unknown>);
  const baseColumn = String(
    metricInfo?.column_name ?? fieldInfo.column_name ?? fieldInfo.column ?? '',
  );
  const aggregate = getAggregateValue(
    metricInfo as { aggregate?: string },
  );
  const prefixedColumn = aggregate ? `${aggregate}_${baseColumn}` : '';

  if (Array.isArray(columnSort)) {
    if (
      columnSort.some(
        item =>
          !isColumnSortDimension(item) && item.column === prefixedColumn,
      )
    ) {
      return prefixedColumn;
    }
    if (
      columnSort.some(
        item => !isColumnSortDimension(item) && item.column === baseColumn,
      )
    ) {
      return baseColumn;
    }
  }

  const hasDuplicate = hasSameDimensionField({
    fieldInfo,
    columnDimensions,
    columnSort,
  });
  return buildMetricDisplayInfo(
    {
      ...(metricInfo as object),
      aggregate:
        getAggregateValue(metricInfo as { aggregate?: string }) ||
        fieldInfo.aggregate,
    },
    hasDuplicate,
  ).column;
}

export function snapshotToMetricSortItem(
  snap: {
    table_id: string;
    table_code: string;
    column_name: string;
    column_display: string;
    column_display_en: string;
    aggregate?: string;
  },
  columnDimensions: unknown[] | undefined,
  columnSort: IColumnSortItem[],
): IColumnSortItem {
  const fieldInfo: IColumnSortFieldInfo = {
    table_id: snap.table_id,
    table_code: snap.table_code || 't0',
    column_name: snap.column_name,
    column_display: snap.column_display,
    column_display_en: snap.column_display_en,
    aggregate: snap.aggregate,
  };
  const hasDuplicate = hasSameDimensionField({
    fieldInfo,
    columnDimensions,
    columnSort,
  });
  const display = buildMetricDisplayInfo(
    {
      column_name: snap.column_name,
      column_display: snap.column_display,
      column_display_en: snap.column_display_en,
      aggregate: snap.aggregate,
    },
    hasDuplicate,
  );
  return {
    table_id: snap.table_id,
    table_code: snap.table_code || 't0',
    column: display.column,
    builder_type: COLUMN_SORT_BUILDER_METRIC,
    column_display_cn: display.column_display_cn,
    column_display_en: display.column_display_en,
  };
}

/** QB 拖拽字段行 → column_sort 项（buildFromQB 与维度/指标构建共用） */
export function qbRowToColumnSortItem(
  raw: unknown,
  builderType: TBuilderType,
  options?: { is_compare_date?: boolean },
): IColumnSortItem | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const column = String(row.column_name ?? row.column ?? row.id ?? '');
  if (!column) {
    return null;
  }
  return normalizeColumnSortItem({
    table_id: String(row.table_id ?? ''),
    table_code: String(row.table_code ?? 't0'),
    column,
    builder_type: builderType,
    column_display_cn: String(row.column_display ?? column),
    column_display_en: String(row.column_display_en ?? column),
    sql_expression: row.sql_expression as string | undefined,
    is_compare_date: options?.is_compare_date,
  });
}
